// Build-time configuration, with one runtime override.
//
// §14.3.6 The desktop shell serves the Gridd embed and the Gridd Library from loopback ports
// it only knows once it has bound them, so their base URLs cannot be build-time constants
// there. The preload exposes `window.__slidecraftConfig` (a narrow, named object — not
// general config injection into the page) and the two `*_BASE_URL` constants below prefer it,
// falling back to the DefinePlugin value everywhere else. The website is unaffected: there is
// no such global, so it reads exactly what it always did.
//
// `__GRIDD_EMBED_BASE_URL__` is injected by webpack's
// DefinePlugin (see webpack.config.js). The `typeof` guard never throws — even for an
// undeclared identifier — so any non-webpack context (tests) or a stale bundle falls
// back to the default instead of crashing. It must resolve to a DIFFERENT origin than
// SlideCraft's own, or the embed silently drops edits (Constraint A).
import { desktopConfig } from "@/lib/desktopBridge";

declare const __GRIDD_EMBED_BASE_URL__: string;

const BUILD_GRIDD_EMBED_BASE_URL =
  typeof __GRIDD_EMBED_BASE_URL__ !== "undefined"
    ? __GRIDD_EMBED_BASE_URL__
    : "https://localhost:3000/embeddable.html";

export const GRIDD_EMBED_BASE_URL =
  desktopConfig()?.griddEmbedBaseUrl || BUILD_GRIDD_EMBED_BASE_URL;

// Origin of the DeckAI add-in that serves the embeddable Gridd Library (TODO §8.1/§8.4) —
// a third embed origin, distinct from SlideCraft's own and from the Gridd embed. The
// signed-in user's Firebase ID token crosses this boundary (§8.3.4), so it must resolve to
// the trusted `black-ground` origin. Injected as a bare global like the embed URL above;
// the `typeof` guard falls back to the deployed add-in when no build-time value is set.
declare const __GRIDD_LIBRARY_BASE_URL__: string;

const BUILD_GRIDD_LIBRARY_BASE_URL =
  typeof __GRIDD_LIBRARY_BASE_URL__ !== "undefined"
    ? __GRIDD_LIBRARY_BASE_URL__
    : "https://black-ground-08064670f.1.azurestaticapps.net/taskpane.html";

export const GRIDD_LIBRARY_BASE_URL =
  desktopConfig()?.griddLibraryBaseUrl || BUILD_GRIDD_LIBRARY_BASE_URL;

// Firebase web config for the studio's Auth session (TODO §9.1.1). Not secret, but injected
// via DefinePlugin like the URLs above rather than hardcoded in source. The `typeof` guard
// falls back to the known `gridd-cb9b6` project config so non-webpack contexts (tests) and
// stale bundles still resolve a valid config. `authDomain` is the project's hosted auth
// handler (`*.firebaseapp.com`) — the standard target for `signInWithPopup` in a browser.
declare const __FIREBASE_CONFIG__: {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
};

export const FIREBASE_CONFIG =
  typeof __FIREBASE_CONFIG__ !== "undefined"
    ? __FIREBASE_CONFIG__
    : {
        apiKey: "AIzaSyBa74EtHu8GKx_Uvn38DH9vP-m0m0sTPxo",
        authDomain: "gridd-cb9b6.firebaseapp.com",
        projectId: "gridd-cb9b6",
        storageBucket: "gridd-cb9b6.firebasestorage.app",
        messagingSenderId: "1016293206323",
        appId: "1:1016293206323:web:a0b54200243e6cb9527646",
        measurementId: "G-2SMZNR5NFJ",
      };

// Provider settings for the AI terminal (packages/react-terminal). The control calls the
// configured LLM API directly from the browser with no backend of ours in the path, so the
// keys below are the *user's own* — injected at build time (see webpack.config.js) rather
// than hardcoded. They default to empty: with no key the cloud providers are inert (the
// terminal reports the missing key), while Ollama needs no key and works against a local
// model out of the box. To enable a cloud provider, build/serve with e.g.
// `ANTHROPIC_API_KEY=… npm run dev`. The `typeof` guard keeps non-webpack contexts (tests)
// from throwing on the undeclared global.
declare const __LLM_TERMINAL_CONFIG__: {
  anthropicApiKey: string;
  anthropicModel: string;
  openaiApiKey: string;
  openaiModel: string;
  ollamaBaseUrl: string;
  ollamaModel: string;
  // Docker Model Runner (Qwen &c.) — a keyless, OpenAI-compatible local endpoint. The host
  // default is :12434/engines/v1; models are namespaced like `ai/qwen3.6`.
  qwenBaseUrl: string;
  qwenModel: string;
};

export const LLM_TERMINAL_CONFIG =
  typeof __LLM_TERMINAL_CONFIG__ !== "undefined"
    ? __LLM_TERMINAL_CONFIG__
    : {
        anthropicApiKey: "",
        anthropicModel: "claude-opus-4-8",
        openaiApiKey: "",
        openaiModel: "gpt-4o-mini",
        ollamaBaseUrl: "http://localhost:11434",
        ollamaModel: "llama3.2",
        qwenBaseUrl: "/__dmr/engines/v1",
        qwenModel: "ai/qwen3.6",
      };
